/**
 * <ActivationLandingPage> — l'arrivée du lien d'activation (page).
 *
 * Où atterrit un parent depuis un mail ou un SMS, identifié par la signature du lien et jamais
 * invité à se connecter (`ActivationLandingScreen.js`). Le lien n'expire pas : la page raconte la
 * situation à l'INSTANT DU CLIC, jamais ce que le mail annonçait.
 *
 * Contrôlée de bout en bout : les six états de `activation/landingState.js:69-93` n'arrivent pas
 * en drapeau mais en CHARGE, et chacun a sa story.
 *
 * | État                   | Ce qu'on passe                                     |
 * |------------------------|----------------------------------------------------|
 * | `activable`            | `nextStep` en variante `activate`                  |
 * | `already_planned`      | `nextStep` en variante `planned`                   |
 * | `reprise`              | `notice` avec son `actionLabel`                    |
 * | `paused`               | `notice` sans action                               |
 * | `nothing_to_activate`  | `notice` sans action                               |
 * | `unavailable`          | `outcome` — il remplace toute la page              |
 *
 * La carte d'offre est celle du portail (`<SubscriptionNextStepCard>`), pas une variante : même
 * coque, même registre, mêmes mots. La landing ne passe simplement aucun trou de couverture — il
 * appartient au portail.
 */
import React from "react"
import { StyleSheet } from "react-native-unistyles"

import { ActivationProductCard } from "../../molecules/ActivationProductCard"
import { ActivationStudentHeader } from "../../molecules/ActivationStudentHeader"
import { AlertCard } from "../../molecules/AlertCard"
import { Button } from "../../atoms/Buttons/Button"
import { FlowOutcome } from "../../molecules/FlowOutcome"
import { PageTemplate } from "../../templates/PageTemplate"
import { Stack } from "../../atoms/containers/Stack"
import { SubscriptionNextStepCard } from "../../organisms/SubscriptionNextStepCard"
import {
    type UIActivationNotice,
    type UIActivationOutcome,
    type UIActivationProduct,
    type UISubscriptionNextStep,
} from "../../../ports/primary/ui/types"
import { ActivationLandingSkeleton } from "./ActivationLandingSkeleton"

/**
 * La colonne de l'arrivée — 560 px, la largeur du legacy. Ce n'est pas un tableau de bord comme
 * les pages de l'espace : une seule carte porte la décision, l'étaler la noierait.
 */
export const ACTIVATION_CONTENT_WIDTH = 560

/** La seule sortie de la page dans tous ses états — elle mène quelque part, ou elle ne sert à rien. */
const OPEN_SPACE_LABEL = "Ouvrir mon espace"

export type ActivationLandingPageProps = {
    /** Prénom de l'élève — « votre enfant » quand le back n'en donne pas (le view-model le pose). */
    studentFirstName: string
    /** Le produit d'appel. Absent → la carte disparaît, la page garde son en-tête. */
    product?: UIActivationProduct | null
    /** La carte « Et ensuite ? » — états `activable` et `already_planned`. */
    nextStep?: UISubscriptionNextStep | null
    /** L'avis — états `reprise`, `paused` et `nothing_to_activate`. */
    notice?: UIActivationNotice | null
    /** Plein écran : lien illisible, ou offre que l'évaluateur n'a pas su lire. */
    outcome?: UIActivationOutcome | null

    /** « Activer la suite » — ouvre le composeur. */
    onActivate?: () => void
    /** « Modifier la reprise » — le même composeur, sur un jalon déjà posé. */
    onEdit?: () => void
    /** L'action de l'avis — « Reprendre l'accompagnement » ouvre le funnel de la saison. */
    onNoticeAction?: () => void
    /** « Ouvrir mon espace ». Absent → le bouton disparaît, et la page devient une impasse. */
    onOpenSpace?: () => void

    /** Le contexte n'est pas encore arrivé — la page montre sa forme (`<ActivationLandingSkeleton>`). */
    loading?: boolean
    testID?: string
}

export function ActivationLandingPage(props: ActivationLandingPageProps) {
    const {
        studentFirstName,
        product,
        nextStep,
        notice,
        outcome,
        onActivate,
        onEdit,
        onNoticeAction,
        onOpenSpace,
        loading = false,
        testID,
    } = props

    // Le plein écran centre son bouton : il n'a pas de carte à border, donc rien à étirer.
    const openSpace = onOpenSpace != null && <Button label={OPEN_SPACE_LABEL} onPress={onOpenSpace} />

    return (
        <PageTemplate
            testID={testID}
            loading={loading}
            skeleton={<ActivationLandingSkeleton />}
            padding="4"
            maxContentWidth={ACTIVATION_CONTENT_WIDTH}
        >
            {outcome != null ? (
                <Stack gap="4" style={styles.outcome}>
                    <FlowOutcome
                        iconName="exclamation-circle"
                        tone="warning"
                        center
                        message={outcome.title}
                        detail={outcome.body}
                    >
                        {openSpace}
                    </FlowOutcome>
                </Stack>
            ) : (
                <Stack gap="3">
                    <ActivationStudentHeader firstName={studentFirstName} />

                    <ActivationProductCard product={product} />

                    {notice != null && (
                        <AlertCard
                            severity={notice.actionLabel != null ? "success" : "info"}
                            title={notice.title}
                            message={notice.body}
                            actions={
                                <Stack gap="2">
                                    {/* L'action principale prend le bouton plein ; le renvoi vers
                                        l'espace redescend en secondaire — deux boutons pleins côte
                                        à côte ne diraient plus lequel la page recommande. */}
                                    {notice.actionLabel != null && onNoticeAction != null && (
                                        <Button label={notice.actionLabel} fullWidth onPress={onNoticeAction} />
                                    )}
                                    {onOpenSpace != null && (
                                        <Button
                                            label={OPEN_SPACE_LABEL}
                                            variant={notice.actionLabel != null ? "outline" : "solid"}
                                            fullWidth
                                            onPress={onOpenSpace}
                                        />
                                    )}
                                </Stack>
                            }
                        />
                    )}

                    {nextStep != null && (
                        <SubscriptionNextStepCard
                            nextStep={nextStep}
                            onActivate={onActivate}
                            onEdit={onEdit}
                            editLabel="Modifier la reprise"
                        />
                    )}
                </Stack>
            )}
        </PageTemplate>
    )
}

const styles = StyleSheet.create(() => ({
    /** Le plein écran occupe la hauteur disponible et se pose au milieu. */
    outcome: { flexGrow: 1, justifyContent: "center" },
}))
